import { Route, UrlMatchResult, UrlSegment } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { CalculationComponent } from './features/featureTest/components/calculation/calculation.component';
import { RoleGuard } from './core/auth/guards/role.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  { path: 'calculation', component: CalculationComponent },
  {
    path: 'model-intellisense',
    loadChildren: () =>
      import('app/features/model-intellisense/model-intellisense.module').then(
        m => m.ModelIntellisenseModule
      )
  },
  { path: '', pathMatch: 'full', redirectTo: 'home' },
  {
    path: 'forbidden-access',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('app/features/auth/forbidden-Access/forbidden-access.module').then(
        m => m.ForbiddenAccessModule
      )
  },
  // Redirect signed in user to the '/example'
  //
  // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'home', canLoad: [AuthGuard] },

  // Tolerate malformed callback paths like /verification%20??%20Url.Content(...
  {
    matcher: (url: UrlSegment[]): UrlMatchResult | null => {
      if (!url.length) {
        return null;
      }

      let firstSegment = url[0].path;
      try {
        firstSegment = decodeURIComponent(firstSegment);
      } catch {
        // Keep raw segment when decode fails
      }

      const normalized = firstSegment.replace(/\s+/g, '').toLowerCase();
      return normalized === 'verification' ? { consumed: [url[0]] } : null;
    },
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    loadChildren: () =>
      import('app/features/auth/external-sign-in/external-sign-in.module').then(
        m => m.ExternalSignInModule
      )
  },

  // External verification route (must stay accessible even when already authenticated)
  {
    path: 'verification',
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    loadChildren: () =>
      import('app/features/auth/external-sign-in/external-sign-in.module').then(
        m => m.ExternalSignInModule
      )
  },
  {
    path: 'verification/:token',
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    loadChildren: () =>
      import('app/features/auth/external-sign-in/external-sign-in.module').then(
        m => m.ExternalSignInModule
      )
  },

  // Auth routes for guests
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () =>
          import('app/features/auth/confirmation-required/confirmation-required.module').then(
            m => m.AuthConfirmationRequiredModule
          )
      },
      {
        path: 'forgot-password',
        loadChildren: () =>
          import('app/features/auth/forgot-password/forgot-password.module').then(
            m => m.AuthForgotPasswordModule
          )
      },
      {
        path: 'sign-in',
        loadChildren: () =>
          import('app/features/auth/sign-in/sign-in.module').then(m => m.AuthSignInModule)
      },
      {
        path: 'sign-up',
        loadChildren: () =>
          import('app/features/auth/sign-up/sign-up.module').then(m => m.AuthSignUpModule)
      },
      {
        path: 'reset-password/:email/:code/:operation',
        loadChildren: () =>
          import('app/features/auth/reset-password/reset-password.module').then(
            m => m.ResetPasswordModule
          )
      }
    ]
  },

  // Auth routes for authenticated users
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty'
    },
    children: [
      {
        path: 'sign-out',
        loadChildren: () =>
          import('app/features/auth/sign-out/sign-out.module').then(m => m.AuthSignOutModule)
      },
      {
        path: 'not-found',
        loadChildren: () =>
          import('app/features/auth/not-found/not-found.module').then(m => m.NotFoundModule)
      },
      {
        path: 'unlock-session',
        loadChildren: () =>
          import('app/features/auth/unlock-session/unlock-session.module').then(
            m => m.AuthUnlockSessionModule
          )
      },
      {
        path: 'external-access/:email/:page/:jwt',
        loadChildren: () =>
          import('app/features/external-access/external-access.module').then(
            m => m.ExternalAccessModule
          )
      }
    ]
  },

  // Landing routes
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty'
    } /*,
        children   : [
            {path: 'home', loadChildren: () => import('app/features/landing/home/home.module').then(m => m.LandingHomeModule), canLoad: [AuthGuard]},
        ]*/
  },

  // Admin routes
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    canLoad: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: InitialDataResolver
    },
    children: [
      {
        path: 'home',
        loadChildren: () => import('app/features/home/home.module').then(m => m.HomeModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'alpha_credit/alpha_credit_cee_operations',
        loadChildren: () =>
          import('app/features/alpha-credit/alpha-credit-cee-operations.module').then(
            m => m.AlphaCreditCeeOperationsModule
          ),
        canLoad: [AuthGuard]
      },
      {
        path: 'traitement',
        loadChildren: () =>
          import('app/features/traitement/traitement.module').then(m => m.TraitementModule),
        // Use canActivate so deep-link refresh doesn't get blocked by canLoad with partial app state
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'traitement/renovation-projects/dossiers_en_cours',
        loadChildren: () =>
          import('app/features/traitement/renovation-projects/dossiers/current-dossier.module').then(
            m => m.CurrentDossierModule
          ),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'traitement/renovation-projects/traitement_dossier_en_cours',
        loadChildren: () =>
          import(
            'app/features/traitement/renovation-projects/dossiers/processing/current-dossier-processing.module'
          ).then(m => m.CurrentDossierProcessingModule),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'traitement/renovation-projects/projet_avant_travaux_a_traiter',
        loadChildren: () =>
          import(
            'app/features/traitement/renovation-projects/renovation-project-before-renovation/renovation-project-before-renovation-to-do.module'
          ).then(m => m.RenovationProjectBeforeRenovationToDoModule),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'traitement/renovation-projects/projet_avant_travaux_a_traiter/projet_avant_travaux_traitement',
        loadChildren: () =>
          import(
            'app/features/traitement/renovation-projects/renovation-project-before-renovation/processing/renovation-project-before-renovation-processing.module'
          ).then(m => m.RenovationProjectBeforeRenovationProcessingModule),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'traitement/renovation-projects/projet_avant_travaux_traites',
        loadChildren: () =>
          import(
            'app/features/traitement/renovation-projects/renovation-project-before-renovation/renovation-project-before-renovation-done.module'
          ).then(m => m.RenovationProjectBeforeRenovationDoneModule),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'traitement/renovation-projects/projet_apres_travaux',
        loadChildren: () =>
          import(
            'app/features/traitement/renovation-projects/renovation-project-after-renovation/renovation-project-after-work.module'
          ).then(m => m.RenovationProjectAfterWorkModule),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'traitement/renovation-projects/projet_fiche_informations',
        loadChildren: () =>
          import(
            'app/features/traitement/renovation-projects/details/renovation-project-details.module'
          ).then(m => m.RenovationProjectDetailsModule),
        canActivate: [AuthGuard, RoleGuard]
      },      
      {
        path: 'controle_qualite',
        loadChildren: () =>
          import('app/features/controle-qualite/controle-qualite.module').then(
            m => m.ControleQualiteModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'controle_qualite/controle_par_contact',
        loadChildren: () =>
          import('app/features/controle-qualite/contact-audit/contact-audit.module').then(
            m => m.ContactAuditModule
          ),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'controle_qualite/controles_externes_indirect',
        loadChildren: () =>
          import('app/features/controle-qualite/external-audit/external-audit.module').then(
            m => m.ExternalAuditModule
          ),
        // Use canActivate so deep-link refresh doesn't get blocked by canLoad with partial app state
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'controle_qualite/controles_externes_indirect/add_edit_controles_externes_indirect',
        loadChildren: () =>
          import(
            'app/features/controle-qualite/external-audit/add-edit/add-edit-external-audit.module'
          ).then(m => m.AddEditExternalAuditModule),
        // Use canActivate so deep-link refresh doesn't get blocked by canLoad with partial app state
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'controle_qualite/suivi_sav',
        loadChildren: () =>
          import('app/features/controle-qualite/after-sales/after-sales.module').then(
            m => m.AfterSalesModule
          ),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'controle_qualite/control_monitoring',
        loadChildren: () =>
          import(
            'app/features/controle-qualite/control-monitoring/control-monitoring.module'
          ).then(m => m.ControlMonitoringModule),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'gestion_commerciale',
        loadChildren: () =>
          import('app/features/gestion-commerciale/gestion-commerciale.module').then(
            m => m.GestionCommercialeModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'gestion_commerciale/facturation_cofrac',
        loadChildren: () =>
          import(
            'app/features/gestion-commerciale/facturation-cofrac/facturation-cofrac.module'
          ).then(m => m.FacturationCofracModule),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'envoi_pncee',
        loadChildren: () =>
          import('app/features/envoi-pncee/envoi-pncee.module').then(m => m.EnvoiPnceeModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/comparaison-acep-tera',
        loadComponent: () =>
          import('app/features/parametrage/acep-tera-comparison/comparison.component').then(m => m.ComparisonComponent),
        canActivate: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage',
        loadChildren: () =>
          import('app/features/parametrage/parametrage.module').then(m => m.ParametrageModule),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },

      {
        path: 'parametrage/acteurs',
        loadChildren: () =>
          import('app/features/parametrage/acteurs/acteurs.module').then(m => m.ActeursModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/partenaires',
        loadChildren: () =>
          import('app/features/parametrage/acteurs/partenaires/partenaires.module').then(
            m => m.PartenairesModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/partenaires/partenaire_contacts',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/partenaires/contacts/partenaire-contacts.module'
          ).then(m => m.PartenaireContactsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/partenaires/partenaire_profils',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/partenaires/profils/partenaire-profils.module'
          ).then(m => m.PartenaireProfilsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/partenaires/partenaire_secteurs_activite',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/partenaires/secteurs-activite/partenaire-secteurs-activite.module'
          ).then(m => m.PartenaireActivitySectorsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/partenaires/partenaire_qualifications',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/partenaires/qualifications/partenaire-qualifications.module'
          ).then(m => m.PartenaireQualificationsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/partenaires/partenaire_soustraitants',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/partenaires/sous-traitants/partenaire-soustraitants.module'
          ).then(m => m.PartenaireSousTraitantsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/partenaires/partenaire_organismes_controle',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/partenaires/organismes-controle/partner-control-offices.module'
          ).then(m => m.PartnerControlOfficesModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/obliges',
        loadChildren: () =>
          import('app/features/parametrage/acteurs/obliges/obliges.module').then(
            m => m.ObligesModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/obliges/oblige_contacts',
        loadChildren: () =>
          import('app/features/parametrage/acteurs/obliges/contacts/oblige-contacts.module').then(
            m => m.ObligeContactsModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/obliges/oblige_secteurs_activite',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/obliges/secteurs-activite/oblige-secteurs-activite.module'
          ).then(m => m.ObligeActivitySectorsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/mandataires',
        loadChildren: () =>
          import('app/features/parametrage/acteurs/mandataires/mandataires.module').then(
            m => m.MandatairesModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/mandataires/mandataire_contacts',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/mandataires/contacts/mandataire-contacts.module'
          ).then(m => m.MandataireContactsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/superviseurs',
        loadChildren: () =>
          import('app/features/parametrage/acteurs/superviseurs/superviseurs.module').then(
            m => m.SuperviseursModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/superviseurs/superviseur_contacts',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/superviseurs/contacts/superviseur-contacts.module'
          ).then(m => m.SuperviseurContactsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/superviseurs/superviseur_mandataires',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/superviseurs/mandataires/superviseur-mandataires.module'
          ).then(m => m.SuperviseurMandatairesModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/apporteurs',
        loadChildren: () =>
          import('app/features/parametrage/acteurs/apporteurs/apporteurs.module').then(
            m => m.ApporteursModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/apporteurs/apporteur_contacts',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/apporteurs/contacts/apporteur-contacts.module'
          ).then(m => m.ApporteurContactsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/soustraitants',
        loadChildren: () =>
          import('app/features/parametrage/acteurs/soustraitants/soustraitants.module').then(
            m => m.SousTraitantsModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/soustraitants/soustraitant_contacts',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/soustraitants/contacts/soustraitant-contacts.module'
          ).then(m => m.SousTraitantContactsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/soustraitants/soustraitant_qualifications',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/soustraitants/qualifications/soustraitant-qualifications.module'
          ).then(m => m.SousTraitantQualificationsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/soustraitants/soustraitant_partenaires',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/soustraitants/partenaires/soustraitant-partenaires.module'
          ).then(m => m.SousTraitantPartenairesModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/bureaux_etudes',
        loadChildren: () =>
          import('app/features/parametrage/acteurs/bureaux-etudes/design-office.module').then(
            m => m.DesignOfficeModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/bureaux_etudes/bureau_etudes_qualifications',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/bureaux-etudes/qualifications/design-office-qualifications.module'
          ).then(m => m.DesignOfficeQualificationsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/organismes_controle',
        loadChildren: () =>
          import('app/features/parametrage/acteurs/organismes-controle/control-office.module').then(
            m => m.ControlOfficeModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/acteurs/organismes_controle/organisme_controle_partenaires',
        loadChildren: () =>
          import(
            'app/features/parametrage/acteurs/organismes-controle/partenaires/control-office-partners.module'
          ).then(m => m.ControlOfficePartnersModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/contrats',
        loadChildren: () =>
          import('app/features/parametrage/contrats/contrats.module').then(m => m.ContratsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits',
        loadChildren: () =>
          import('app/features/parametrage/operations-produits/operations-produits.module').then(
            m => m.OperationsProduitsModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/produits',
        loadChildren: () =>
          import('app//features/parametrage/operations-produits/produits/product.module').then(
            m => m.ProductModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/produits/produit_caracteristiques',
        loadChildren: () =>
          import(
            'app//features/parametrage/operations-produits/produits/technical-characteristic/technical-characteristic-values.module'
          ).then(m => m.TechnicalCharacteristicValuesModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/cee-operation-file.module'
          ).then(m => m.CeeOperationFileModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_distinctions_cas',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/distinction-cas/cee-operation-file-distinct-case.module'
          ).then(m => m.CeeOperationFileDistinctCaseModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_champs',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/champ/cee-operation-file-field.module'
          ).then(m => m.CeeOperationFileFieldModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_champs_mapping',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/champ/mapping/cee-operation-file-field-mapping.module'
          ).then(m => m.CeeOperationFileFieldMappingModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_balises',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/balise/cee-operation-file-balise.module'
          ).then(m => m.CeeOperationFileBaliseModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_balises_mapping',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/balise/mapping/cee-operation-file-balise-mapping.module'
          ).then(m => m.CeeOperationFileBaliseMappingModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_css',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/obligation-controle-css/cee-operation-file-css.module'
          ).then(m => m.CeeOperationFileCssModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_css_exceptions',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/obligation-controle-css/exceptions/cee-operation-file-css-exception.module'
          ).then(m => m.CeeOperationFileCssExceptionModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_cpc',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/obligation-controle-cpc/cee-operation-file-cpc.module'
          ).then(m => m.CeeOperationFileCpcModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_traitements_specifiques_dova',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/traitement-specifique-dova/cee-operation-file-dova-spec.module'
          ).then(m => m.CeeOperationFileDovaSpecModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_qualifications_partenaire',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/qualification-partenaire/cee-operation-file-partner-qualification-type.module'
          ).then(m => m.CeeOperationFilePartnerQualificationTypeModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_documents_obligatoires',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/documents-obligatoires/cee-operation-file-document-type.module'
          ).then(m => m.CeeOperationFileDocumentTypeModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_qualifications_bureau_etude',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/qualification-bureau-etude/cee-operation-file-design-office-qualification-type.module'
          ).then(m => m.CeeOperationFileDesignOfficeQualificationTypeModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_produits',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-cee/produit/cee-operation-file-product.module'
          ).then(m => m.CeeOperationFileProductModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_cee/fiche_operation_cee_conditions_eligibilite',
        loadChildren: () =>
          import('app/shared/screens/eligibility-conditions/eligibility-condition.module').then(
            m => m.EligibilityConditionModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_mpr',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-mpr/mpr-operation-file.module'
          ).then(m => m.MprOperationFileModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_mpr/fiche_operation_mpr_distinctions_cas',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-mpr/distinction-cas/mpr-operation-file-distinct-case.module'
          ).then(m => m.MprOperationFileDistinctCaseModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_mpr/fiche_operation_mpr_champs',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-mpr/champ/mpr-operation-file-field.module'
          ).then(m => m.MprOperationFileFieldModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_mpr/fiche_operation_mpr_champs_mapping',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-mpr/champ/mapping/mpr-operation-file-field-mapping.module'
          ).then(m => m.MprOperationFileFieldMappingModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_mpr/fiche_operation_mpr_qualifications_partenaire',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-mpr/qualification-partenaire/mpr-operation-file-partner-qualification-type.module'
          ).then(m => m.MprOperationFilePartnerQualificationTypeModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_mpr/fiche_operation_mpr_produits',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-mpr/produit/mpr-operation-file-product.module'
          ).then(m => m.MprOperationFileProductModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_mpr/fiche_operation_mpr_plafonds',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/fiches-operation-mpr/plafond/mpr-operation-file-ceiling.module'
          ).then(m => m.MprOperationFileCeilingModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/fiches_operation_mpr/fiche_operation_mpr_conditions_eligibilite',
        loadChildren: () =>
          import('app/shared/screens/eligibility-conditions/eligibility-condition.module').then(
            m => m.EligibilityConditionModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/modeles_document',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/modeles-document/document-template.module'
          ).then(m => m.DocumentTemplateModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/parametres_metier',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/parametres-metier/parametres-metier.module'
          ).then(m => m.ParametreMetierModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/parametres_metier/parametre_metier_detail',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/parametres-metier/detail/detail-parametre-metier.module'
          ).then(m => m.DetailParametreMetierModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/operations_generiques',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/operations-generiques/generic-operation.module'
          ).then(m => m.GenericOperationModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/operations_generiques/operation_generique_balises_entree',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/operations-generiques/balise-in/generic-operation-balise-in.module'
          ).then(m => m.GenericOperationBaliseInModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/operations_generiques/operation_generique_balises_sortie',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/operations-generiques/balise-out/generic-operation-balise-out.module'
          ).then(m => m.GenericOperationBaliseOutModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/operations_generiques/operation_generique_mapping_interne',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/operations-generiques/internal-mapping/generic-operation-internal-mapping.module'
          ).then(m => m.GenericOperationInternalMappingModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/operations_generiques/operation_generique_produits_generiques',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/operations-generiques/generic-products/generic-operation-generic-products.module'
          ).then(m => m.GenericOperationGenericProductsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/operations_generiques/operation_generique_produit_generique_balises_entree',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/operations-generiques/generic-products/generic-operation-generic-product-baliseIn/generic-operation-generic-product-balise-in.module'
          ).then(m => m.GenericOperationGenericProductBaliseInModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/operations_generiques/operation_generique_produit_generique_balises_sortie',
        loadChildren: () =>
          import(
            'app/features/parametrage/operations-produits/operations-generiques/generic-products/generic-operation-generic-product-baliseOut/generic-operation-generic-product-balise-out.module'
          ).then(m => m.GenericOperationGenericProductBaliseOutModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/produits_generiques',
        loadChildren: () =>
          import(
            'app//features/parametrage/operations-produits/produits-generiques/generic-product.module'
          ).then(m => m.GenericProductModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/produits_generiques/produit_generique_categories',
        loadChildren: () =>
          import(
            'app//features/parametrage/operations-produits/produits-generiques/category/generic-product-category.module'
          ).then(m => m.GenericProductCategoryModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/produits_generiques/produit_generique_caracteristiques',
        loadChildren: () =>
          import(
            'app//features/parametrage/operations-produits/produits-generiques/technical-characteristic/generic-product-technical-characteristics.module'
          ).then(m => m.GenericProductTechnicalCharacteristicsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/operations_produits/produits_generiques/produit_generique_libelles_caracteristiques_additionnelles',
        loadChildren: () =>
          import(
            'app//features/parametrage/operations-produits/produits-generiques/additional-CT-label/generic-product-additional-CT-label.module'
          ).then(m => m.GenericProductAdditionalCTLabelModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/utilisateurs_acces',
        loadChildren: () =>
          import('app/features/parametrage/utilisateurs_acces/utilisateurs_acces.module').then(
            m => m.UtilisateurAccesModule
          ),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path: 'parametrage/utilisateurs_acces/utilisateurs',
        loadChildren: () =>
          import(
            'app/features/parametrage/utilisateurs_acces/utilisateurs/utilisateurs.module'
          ).then(m => m.UtilisateursModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/utilisateurs_acces/utilisateurs/permissions',
        loadChildren: () =>
          import(
            'app/features/parametrage/utilisateurs_acces/utilisateurs/permissions/utilisateur-permissions.module'
          ).then(m => m.UtilisateurPermissionsModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/utilisateurs_acces/equipes_gestionnaires',
        loadChildren: () =>
          import(
            'app/features/parametrage/utilisateurs_acces/equipes-gestionnaires/user-team.module'
          ).then(m => m.UserTeamModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/utilisateurs_acces/equipes_gestionnaires/equipe_gestionnaires_gestionnaires',
        loadChildren: () =>
          import(
            'app/features/parametrage/utilisateurs_acces/equipes-gestionnaires/gestionnaires/user-team-user.module'
          ).then(m => m.UserTeamUserModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/utilisateurs_acces/equipes_gestionnaires/equipe_gestionnaires_partenaires',
        loadChildren: () =>
          import(
            'app/features/parametrage/utilisateurs_acces/equipes-gestionnaires/partenaires/user-team-actor.module'
          ).then(m => m.UserTeamActorModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/parametrage_traitement',
        loadChildren: () =>
          import(
            'app/features/parametrage/parametrage-traitement/parametrage-traitement.module'
          ).then(m => m.ParametrageTraitementModule),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path: 'parametrage/parametrage_traitement/renovation_project_fields',
        loadChildren: () =>
          import(
            'app/features/parametrage/parametrage-traitement/renovation-project-fields/renovation-project-fields.module'
          ).then(m => m.RenovationProjectFieldsModule),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path: 'parametrage/parametrage_traitement/processing_steps',
        loadChildren: () =>
          import(
            'app/features/parametrage/parametrage-traitement/processing-step/processing-step.module'
          ).then(m => m.ProcessingStepModule),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path:'parametrage/parametrage_traitement/prioritization_rules',
        loadChildren: () =>
          import(
            'app/features/parametrage/parametrage-traitement/prioritization-rules/prioritization-rule.module'
          ).then(m => m.PrioritizationRuleModule),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path: 'parametrage/parametrage_traitement/tableau_precarite_cee',
        loadChildren: () =>
          import(
            'app/features/parametrage/parametrage-traitement/tableaux-precarite-cee/cee-preca-table.module'
          ).then(m => m.CeePrecarityTableModule),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path: 'parametrage/parametrage_traitement/tableau_precarite_cee/tableau_precarite_cee_detail',
        loadChildren: () =>
          import(
            'app/features/parametrage/parametrage-traitement/tableaux-precarite-cee/detail/detail-cee-preca-table.module'
          ).then(m => m.DetailCeePrecarityTableModule),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path: 'parametrage/parametrage_traitement/tableau_precarite_mpr',
        loadChildren: () =>
          import(
            'app/features/parametrage/parametrage-traitement/tableaux-precarite-mpr/mpr-preca-table.module'
          ).then(m => m.MprPrecarityTableModule),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path: 'parametrage/parametrage_traitement/tableau_precarite_mpr/tableau_precarite_mpr_detail',
        loadChildren: () =>
          import(
            'app/features/parametrage/parametrage-traitement/tableaux-precarite-mpr/detail/detail-mpr-preca-table.module'
          ).then(m => m.DetailMprPrecarityTableModule),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path: 'parametrage/automatisation',
        loadChildren: () =>
          import('app/features/parametrage/automatisation/automatisation.module').then(
            m => m.AutomatisationModule
          ),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path: 'parametrage/automatisation/import_maj_produits',
        loadChildren: () =>
          import(
            'app/features/parametrage/automatisation/import-maj-produits/import-maj-produits.module'
          ).then(m => m.ImportMajProduitsModule),
        canLoad: [AuthGuard],
        canActivate: [RoleGuard]
      },
      {
        path: 'parametrage/parametres_systeme',
        loadChildren: () =>
          import('app/features/parametrage/parametres-systeme/parametres-systeme.module').then(
            m => m.ParametresSystemeModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/parametres_systeme/parametre_systeme_detail',
        loadChildren: () =>
          import(
            'app/features/parametrage/parametres-systeme/detail/detail-parametre-systeme.module'
          ).then(m => m.DetailParametreSystemeModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/utilisateurs_acces/gestion_profils',
        loadChildren: () =>
          import('app/features/parametrage/utilisateurs_acces/profils/profile.module').then(
            m => m.ProfileModule
          ),
        canLoad: [AuthGuard]
      },
      {
        path: 'parametrage/utilisateurs_acces/gestion_profils/permissions',
        loadChildren: () =>
          import(
            'app/features/parametrage/utilisateurs_acces/profils/permissions/permission.module'
          ).then(m => m.PermissionModule),
        canLoad: [AuthGuard]
      },
      {
        path: 'parametrage/utilisateurs_acces/gestion_composants',
        loadChildren: () =>
          import(
            'app/features/parametrage/utilisateurs_acces/composants/teraComponent.module'
          ).then(m => m.TeraComponentModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/integration_externe',
        loadChildren: () =>
          import('app/features/parametrage/integration-externe/external-integration.module').then(
            m => m.ExternalIntegrationModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/integration_externe/champs_emmy',
        loadChildren: () =>
          import('app/features/parametrage/integration-externe/champs-emmy/emmy-field.module').then(
            m => m.EmmyfieldModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/integration_externe/champs_sica',
        loadChildren: () =>
          import('app/features/parametrage/integration-externe/champs-sica/sica-field.module').then(
            m => m.SicaFieldModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/integration_externe/dova',
        loadChildren: () =>
          import('app/features/parametrage/integration-externe/dova/dova.module').then(
            m => m.DovaModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/integration_externe/dova/champs_dova',
        loadChildren: () =>
          import(
            'app/features/parametrage/integration-externe/dova/champs-dova/dova-field.module'
          ).then(m => m.DovaFieldModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/integration_externe/dova/valeurs_extraites_dova',
        loadChildren: () =>
          import(
            'app/features/parametrage/integration-externe/dova/valeurs-extraites-dova/dova-field-extracted-value.module'
          ).then(m => m.DovaFieldExtractedValueModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/integration_externe/cofrac',
        loadChildren: () =>
          import('app/features/parametrage/integration-externe/cofrac/cofrac.module').then(
            m => m.CofracModule
          ),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/integration_externe/cofrac/champs_cofrac',
        loadChildren: () =>
          import(
            'app/features/parametrage/integration-externe/cofrac/champs-cofrac/cofrac-field.module'
          ).then(m => m.CofracFieldModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/integration_externe/cofrac/syntheses_cofrac',
        loadChildren: () =>
          import(
            'app/features/parametrage/integration-externe/cofrac/syntheses-cofrac/cofrac-template.module'
          ).then(m => m.CofracTemplateModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/contrats/contrats_cee_partenaire',
        loadChildren: () =>
          import(
            'app/features/parametrage/contrats/contrats-cee-partenaire/cee-contract-partner.module'
          ).then(m => m.CeeContractPartnerModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/contrats/contrats_cee_mandataire',
        loadChildren: () =>
          import(
            'app/features/parametrage/contrats/contrats-cee-mandataire/cee-contract-mandataire.module'
          ).then(m => m.CeeContractMandataireModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/contrats/contrats_cee_apporteur',
        loadChildren: () =>
          import(
            'app/features/parametrage/contrats/contrats-cee-apporteur/cee-contract-business-provider.module'
          ).then(m => m.CeeContractBusinessProviderModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/contrats/contrats_cee_oblige',
        loadChildren: () =>
          import(
            'app/features/parametrage/contrats/contrats-cee-oblige/cee-contract-oblige.module'
          ).then(m => m.CeeContractObligeModule),
        canLoad: [AuthGuard, RoleGuard]
      },
      {
        path: 'parametrage/contrats/contrats_mpr_partenaire',
        loadChildren: () =>
          import(
            'app/features/parametrage/contrats/contrats-mpr-partenaire/mpr-contract-partner.module'
          ).then(m => m.MprContractPartnerModule),
        canLoad: [AuthGuard, RoleGuard]
      }
    ]
  }
];
